import classnames from 'classnames';
import { useState, ReactElement, useRef, useEffect } from 'react';

import styles from './ReadMoreLess.module.scss';

interface IProps {
  content: string;
  lines?: number;
}

const ReadMoreLess = ({ content, lines = 2 }: IProps): ReactElement => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const [showFull, setShowFull] = useState(false);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    let raf = 0;
    let ro: ResizeObserver | null = null;

    const wrapper = wrapperRef.current;
    if (wrapper) {
      const contentEl = wrapper.querySelector(`.${styles.content}`) as HTMLDivElement | null;

      if (contentEl) {
        let last: boolean | null = null;

        const check = () => {
          const next = contentEl.scrollHeight > contentEl.clientHeight + 1;
          if (last === next) return;
          last = next;
          setIsOverflowed(next);
        };

        check();
        raf = requestAnimationFrame(check);

        ro = new ResizeObserver(check);
        ro.observe(contentEl);
      }
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [content, lines]);

  return (
    <div
      ref={wrapperRef}
      className={classnames(styles.contentContainer, { [styles.full]: showFull })}
      style={!showFull ? ({ ['--clamp-lines' as any]: lines } as React.CSSProperties) : undefined}
    >
      <div className={styles.content}>{content}</div>

      {(isOverflowed || showFull) && (
        <div
          className={classnames(styles.readMoreLessContainer, {
            [styles.full]: showFull,
          })}
        >
          <span
            role="presentation"
            className={styles.readMoreLessToggle}
            onClick={() => setShowFull(prev => !prev)}
          >
            {showFull ? 'Read Less' : 'Read More'}
          </span>
        </div>
      )}
    </div>
  );
};

export default ReadMoreLess;
